import type { SelectedTaxTypeMapping, TaxTypeDetail } from "../../review/types/multiTax.types";
import type { SelectedJurisdiction } from "../../review/types/jurisdiction.types";
import {
    SALES_TAX_KEY,
    SALES_TAX_SOURCE_MAPPING_ID,
} from "../../review/jurisdiction/multiTax/taxTypeKeys";
import { resolveMultiTaxSubTypeSelections } from "./mapMultiTaxSubTypesToSourceMappingIds";
import { mapLocalJurisdictionsToCertificateJurisdictionCodes } from "./mapLocalJurisdictionsToCertificateJurisdictionCodes";

/**
 * Builds the certificate-search `taxSubTaxMappingIds` filter — a flat, OR-matched list of
 * `source_mapping_id`s. Tax type + sub tax type selections both resolve to these ids via the
 * tax-type-details catalog:
 *   - a selected sub tax type contributes its own `source_mapping_id`;
 *   - a selected tax type contributes every `source_mapping_id` under it (match any of its
 *     subtypes) *unless* the user also picked sub tax types under that tax type, in which case the
 *     picked ids are the whole contribution — a narrower selection must not be widened back to the
 *     full tax type;
 *   - the synthetic Sales & Use Tax key contributes the reserved sentinel (-1). SUT has no subtypes
 *     of its own, so it always contributes the sentinel and is never narrowed by a sub tax type
 *     picked under some other tax type.
 * Ids are de-duplicated.
 */
export function buildTaxSubTaxMappingIds(
    multiTaxTypes: string[] | undefined,
    multiTaxSubTypes: string[] | undefined,
    taxTypeDetails: TaxTypeDetail[] | undefined
): number[] {
    const subTypeSelections = resolveMultiTaxSubTypeSelections(multiTaxSubTypes, taxTypeDetails);
    const narrowedTaxTypes = new Set(subTypeSelections.map(selection => selection.taxTypeId));

    const ids = new Set<number>();

    (multiTaxTypes ?? []).forEach(taxType => {
        if (taxType === SALES_TAX_KEY) {
            ids.add(SALES_TAX_SOURCE_MAPPING_ID);
            return;
        }
        // Narrowed by an explicit sub tax type pick — the loop below contributes just those ids.
        if (narrowedTaxTypes.has(taxType)) {
            return;
        }
        taxTypeDetails
            ?.find(detail => detail.taxTypeId === taxType)
            ?.taxSubTypeDetails?.forEach(subType => {
                if (typeof subType.taxTypeMappingId === "number") {
                    ids.add(subType.taxTypeMappingId);
                }
            });
    });

    // Sub tax types filter on their own id, including when their tax type isn't itself selected
    // (the Sub tax type dropdown lists every catalog subtype while no tax type is picked).
    subTypeSelections.forEach(selection => ids.add(selection.sourceMappingId));

    return [...ids];
}

/**
 * Non-SUT multi-tax jurisdiction selections contribute their bare jurisdiction `code` to the
 * search API's `certificateJurisdictionCodes` filter (which the API ANDs with `taxSubTaxMappingIds`).
 */
export function mapMultiTaxJurisdictionsToCodes(
    items: SelectedTaxTypeMapping[] | undefined
): string[] {
    if (!items?.length) {
        return [];
    }
    return items.map(item => item.jurisCode).filter((code): code is string => Boolean(code));
}

/** Fields of the certificate filter state consumed when building the multi-tax request params. */
interface MultiTaxFilterState {
    localJurisdictions?: SelectedJurisdiction[];
    multiTaxJurisdictions?: SelectedTaxTypeMapping[];
    multiTaxTypes?: string[];
    multiTaxSubTypes?: string[];
}

/** The multi-tax portion of a certificate-search request body. */
export interface MultiTaxRequestParams {
    certificateJurisdictionCodes: string[];
    taxSubTaxMappingIds: number[];
}

/**
 * Builds the multi-tax slice of a certificate-search request body — the deduped
 * `certificateJurisdictionCodes` (SUT local jurisdictions + non-SUT multi-tax codes, which the API
 * ANDs with `taxSubTaxMappingIds`) plus the flat `taxSubTaxMappingIds` list. Shared by the live
 * search (`fetchCertificatesSearchAPI`) and export (`certificateFiltersData`) paths so the two
 * request bodies stay in lock-step.
 */
export function buildMultiTaxRequestParams(
    certificateFiltersState: MultiTaxFilterState,
    taxTypeDetails: TaxTypeDetail[] | undefined
): MultiTaxRequestParams {
    return {
        certificateJurisdictionCodes: [
            ...new Set([
                ...mapLocalJurisdictionsToCertificateJurisdictionCodes(
                    certificateFiltersState.localJurisdictions
                ),
                ...mapMultiTaxJurisdictionsToCodes(certificateFiltersState.multiTaxJurisdictions),
            ]),
        ],
        taxSubTaxMappingIds: buildTaxSubTaxMappingIds(
            certificateFiltersState.multiTaxTypes,
            certificateFiltersState.multiTaxSubTypes,
            taxTypeDetails
        ),
    };
}
